import { z } from "zod";
import { orderItemInput } from "./orders";
import { email, phone, trimmed, uuid } from "./common";
import type { GeoPoint } from "./org";

/** Публичный сайт, QR-меню и QR-заказ (ТЗ 07 §7.3, ТЗ 13), онбординг (ТЗ 16 §16.12). */

/** Транслитерация названия в поддомен: «Кафе Нават» → kafe-navat */
const TR: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", ң: "n", о: "o", ө: "o", п: "p", р: "r", с: "s", т: "t",
  у: "u", ү: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya", ғ: "g", қ: "q", ҳ: "h", ў: "o",
};
export function slugifySite(name: string): string {
  const s = name.toLowerCase().split("").map((c) => TR[c] ?? c).join("").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 36);
  return s.length >= 3 ? s : `cafe-${s || "site"}`;
}

/**
 * Служебные поддомены: адреса платформы (app, admin, api — шлюз), почта и DNS, инфраструктура, страницы и бренд Coca.
 * Заведение такой поддомен взять не может — иначе подмена служебного адреса или фишинг от имени платформы.
 */
export const RESERVED_SITE_SLUGS: ReadonlySet<string> = new Set([
  // адреса платформы и её разделы
  "www", "app", "admin", "api", "erp", "pos", "kds", "platform", "panel", "dashboard", "console", "cabinet", "my", "account", "accounts",
  "auth", "login", "logout", "signin", "signup", "register", "oauth", "sso", "id", "invite", "reset-password", "billing", "pay", "payment", "payments", "checkout", "invoice", "invoices",
  "partners", "nearby", "landing", "site", "sites", "web", "menu", "order", "orders", "qr", "m", "mobile", "desktop", "download", "downloads", "update", "updates", "releases",
  "help", "support", "docs", "doc", "learn", "academy", "status", "blog", "news", "about", "contact", "contacts", "careers", "jobs", "legal", "terms", "privacy", "security", "abuse",
  // бренд
  "coca", "cafeos", "kafecoco", "kafe-coco", "official",
  // почта и DNS
  "mail", "email", "webmail", "smtp", "imap", "pop", "pop3", "mx", "ns", "dns", "autodiscover", "autoconfig", "postmaster", "hostmaster", "webmaster", "noreply", "no-reply", "mailer", "newsletter",
  // инфраструктура
  "cdn", "static", "assets", "media", "files", "file", "img", "images", "upload", "uploads", "storage", "s3", "backup", "backups", "db", "redis", "postgres", "mysql",
  "ws", "wss", "socket", "rt", "realtime", "webhook", "webhooks", "hooks", "gateway", "proxy", "relay", "vpn", "ftp", "sftp", "ssh", "git", "gitlab", "github", "ci", "cd", "jenkins",
  "grafana", "prometheus", "metrics", "monitor", "monitoring", "logs", "kibana", "sentry", "health", "internal", "intranet", "system", "server", "servers", "node", "cluster", "host", "localhost", "local",
  "dev", "development", "test", "testing", "qa", "stage", "staging", "preview", "demo", "beta", "alpha", "sandbox", "prod", "production", "uat",
  "root", "administrator", "sysadmin", "owner", "staff", "team", "info", "bot", "telegram", "ai", "crm", "hr", "wiki", "office", "shop", "store",
]);

/** Служебный ли поддомен: из списка, с цифрой на конце (api2, ns1, mail-2, dev01) или punycode (xn--). */
export function isReservedSiteSlug(slug: string): boolean {
  const s = slug.trim().toLowerCase();
  if (RESERVED_SITE_SLUGS.has(s) || s.startsWith("xn--")) return true;
  const base = s.replace(/-?\d+$/, "");
  return base !== s && RESERVED_SITE_SLUGS.has(base);
}

export const siteSlug = z.string().trim().toLowerCase()
  .regex(/^[a-z0-9]([a-z0-9-]{1,38}[a-z0-9])$/, { error: "Поддомен: 3–40 символов, латиница, цифры и дефис" })
  .refine((v) => !isReservedSiteSlug(v), { error: "Это служебный адрес — выберите другой поддомен" });

/** Соцсети заведения на сайте: порядок — порядок показа. */
export const SOCIAL_NETWORKS = ["instagram", "whatsapp", "telegram", "tiktok", "facebook", "youtube"] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

const SOCIAL_BASE: Record<SocialNetwork, (h: string) => string> = {
  instagram: (h) => `https://instagram.com/${h}`,
  whatsapp: (h) => `https://wa.me/${h}`,
  telegram: (h) => `https://t.me/${h}`,
  tiktok: (h) => `https://www.tiktok.com/@${h}`,
  facebook: (h) => `https://facebook.com/${h}`,
  youtube: (h) => `https://youtube.com/@${h}`,
};

/**
 * Ссылка на соцсеть из того, что ввёл владелец: полный адрес (http/https), «@имя» или имя;
 * WhatsApp — номер телефона. Не подходит — null (`javascript:` и прочее не пропускаются).
 */
export function socialUrl(network: SocialNetwork, value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return /^https?:\/\/[^\s/?#]+\.[^\s]+$/i.test(v) && v.length <= 300 ? v : null;
  if (network === "whatsapp") {
    const d = v.replace(/[\s()+-]/g, "");
    return /^[1-9]\d{7,14}$/.test(d) ? SOCIAL_BASE.whatsapp(d) : null;
  }
  const h = v.replace(/^@/, "");
  return /^[A-Za-z0-9._-]{1,100}$/.test(h) ? SOCIAL_BASE[network](h) : null;
}

const optional = <T extends z.ZodType>(t: T) => t.nullable().optional().or(z.literal("").transform(() => null));
const social = (n: SocialNetwork) =>
  optional(z.string().trim().max(300, { error: "Максимум 300 символов" }).refine((v) => !!socialUrl(n, v), { error: n === "whatsapp" ? "Номер WhatsApp в формате +996XXXXXXXXX или ссылка" : "Ссылка или имя аккаунта (@name)" }));

/** Контакты заведения на сайте: что не заполнено — не показывается. */
export const siteContactsSchema = z.strictObject({
  phone: optional(phone),
  email: optional(email),
  ...(Object.fromEntries(SOCIAL_NETWORKS.map((n) => [n, social(n)])) as Record<SocialNetwork, ReturnType<typeof social>>),
});
export type SiteContacts = z.output<typeof siteContactsSchema>;

/** Контакты для сайта (`/public/v1/site`): только заполненные, соцсети — готовыми ссылками. */
export type PublicContacts = { phone: string | null; email: string | null; socials: { network: SocialNetwork; url: string }[] };
export function publicContacts(c: SiteContacts | null | undefined): PublicContacts {
  const socials = SOCIAL_NETWORKS.flatMap((network) => {
    const url = c?.[network] ? socialUrl(network, c[network]!) : null;
    return url ? [{ network, url }] : [];
  });
  return { phone: c?.phone || null, email: c?.email || null, socials };
}

export const siteSettingsSchema = z.strictObject({
  siteSlug: siteSlug.optional(),
  heroTitle: z.string().trim().max(120).nullable().optional(),
  about: z.string().trim().max(2000).nullable().optional(),
  contacts: siteContactsSchema.optional(),
  qrOrdering: z.strictObject({
    enabled: z.boolean(),
    /** Имя гостя обязательно */
    requireName: z.boolean().default(false),
    /** Сразу на кухню (иначе — после подтверждения официантом) */
    autoSendToKitchen: z.boolean().default(true),
  }).optional(),
  seo: z.strictObject({ title: z.string().trim().max(70).nullable().optional(), description: z.string().trim().max(160).nullable().optional() }).optional(),
});
export type SiteSettings = z.output<typeof siteSettingsSchema>;

/** Заказ гостя (POST /public/v1/orders): клиентский id — идемпотентность; за стол — только с QR-токеном. */
export const publicOrderSchema = z.strictObject({
  id: uuid,
  branchSlug: z.string().min(1).max(120),
  tableToken: z.string().min(10).max(64).optional(),
  type: z.enum(["dine_in", "takeaway"]),
  items: z.array(orderItemInput).min(1, { error: "Корзина пуста" }).max(50),
  customerName: trimmed(0, 80).nullable().optional(),
  phone: phone.nullable().optional(),
  comment: trimmed(0, 300).nullable().optional(),
}).refine((v) => v.type !== "dine_in" || !!v.tableToken, { error: "Заказ за стол — по QR-коду стола", path: ["tableToken"] });
export type PublicOrderInput = z.input<typeof publicOrderSchema>;

// ───────── онбординг ─────────

export const VENUE_FORMATS = ["cafe", "coffee", "fast_food", "self_service", "food_court"] as const;
export type VenueFormat = (typeof VENUE_FORMATS)[number];
export const ONBOARDING_STEPS = ["company", "branch", "currency", "menu", "tables", "employees", "cash"] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

export const onboardingCompanySchema = z.strictObject({
  name: trimmed(2, 120),
  country: z.string().regex(/^[A-Z]{2}$/),
  legalName: z.string().trim().max(200).nullable().optional(),
  taxId: z.string().trim().regex(/^\d{10,14}$/, { error: "ИНН — 10–14 цифр" }).nullable().optional().or(z.literal("").transform(() => null)),
});
export const onboardingBranchSchema = z.strictObject({
  name: trimmed(2, 120),
  address: z.string().trim().max(300).nullable().optional(),
  city: z.string().trim().max(80).nullable().optional(),
  phone: phone.nullable().optional().or(z.literal("").transform(() => null)),
  format: z.enum(VENUE_FORMATS),
});
export const onboardingCurrencySchema = z.strictObject({
  currency: z.string().regex(/^[A-Z]{3}$/),
  timezone: z.string().min(3).max(60),
  dayCutoff: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { error: "Формат ЧЧ:ММ" }),
});
export const onboardingMenuSchema = z.strictObject({ template: z.enum(["coffee", "cafe", "fast_food"]) });
export const onboardingTablesSchema = z.strictObject({
  hallName: trimmed(1, 60),
  count: z.number().int().min(1).max(100),
  capacity: z.number().int().min(1).max(30),
});
export const onboardingStepSchema = z.strictObject({ step: z.enum(ONBOARDING_STEPS), skip: z.boolean().default(false) });

/** URL есть и в браузере, и в Node; в shared нет типов DOM/Node — достаточно разбора адреса. */
declare const URL: new (url: string) => { hostname: string };

/**
 * Свой адрес сайта (`addon.custom_domain`, ТЗ 13 §2): «https://Navat.kg/» → «navat.kg», кириллица → punycode.
 * Нужна точка и зона из букв; IP и localhost не подходят.
 */
export const customDomain = z
  .string()
  .trim()
  .min(1, { error: "Обязательное поле" })
  .max(253, { error: "Максимум 253 символа" })
  .transform((v, c) => {
    try {
      return new URL(/^[a-z]+:\/\//i.test(v) ? v : `http://${v}`).hostname.replace(/\.$/, "");
    } catch {
      c.addIssue({ code: "custom", message: "Адрес в формате navat.kg" });
      return z.NEVER;
    }
  })
  .pipe(z.string().regex(/^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/, { error: "Адрес в формате navat.kg" }));
export const customDomainSchema = z.strictObject({ domain: customDomain });
export type CustomDomainInput = z.input<typeof customDomainSchema>;

// ───────── витрина лендинга Coca (GET /public/v1/showcase) ─────────

/** Заведение на платформе с опубликованным сайтом. */
export type ShowcaseVenue = {
  name: string; slug: string; customDomain: string | null; heroTitle: string | null; about: string | null;
  city: string | null; branches: number; logo: string | null; promotions: number;
};
/** Действующая акция заведения (без промокодов — их выдаёт само заведение). */
export type ShowcasePromotion = {
  id: string; venue: { name: string; slug: string; customDomain: string | null }; name: string;
  type: "percent" | "fixed" | "combo" | "bogo" | "gift"; percent: number | null; amount: number | null; buyQty: number | null; getQty: number | null;
  comboPrice: number | null; minSubtotal: number; days: number[]; timeFrom: string | null; timeTo: string | null; dateTo: string | null; currency: string;
};
export type PublicShowcase = { venues: ShowcaseVenue[]; promotions: ShowcasePromotion[]; stats: { venues: number; cities: number; branches: number } };

/** Тариф для лендинга (`GET /public/v1/plans`): цены — minor в `PublicPlans.currency`, null — по договорённости. */
export type PublicPlan = {
  code: string; name: string; trialDays: number;
  prices: { month: number | null; quarter: number | null; year: number | null };
  /** Разовая плата за подключение */
  setupFee: number | null;
  /** Функции (`core.*`, `addon.*`), входящие в тариф */
  features: string[];
  /** Лимиты и квоты: null — без ограничений */
  limits: Record<string, number | null>;
};
export type PublicAddonMode = "included" | "purchasable" | "unavailable";
/** Дополнение или пакет: цена в месяц (у пакета — за единицу), доступность по коду тарифа. */
export type PublicAddon = { code: string; name: string; description: string | null; unitLabel: string | null; month: number | null; modes: Record<string, PublicAddonMode> };
export type PublicPlans = { currency: string; plans: PublicPlan[]; addons: PublicAddon[]; packs: PublicAddon[] };

/** Точка на странице «Рядом» (`GET /public/v1/nearby`): филиал с координатами и действующие акции заведения в нём. */
export type NearbyPlace = {
  /** `{заведение}/{филиал}` — стабильный ключ точки */
  key: string;
  venue: { name: string; slug: string; customDomain: string | null; logo: string | null; tagline: string | null };
  branch: {
    slug: string; name: string; address: string | null; city: string | null; phone: string | null; timezone: string; formats: string[];
    workingHours: Partial<Record<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun", { open: string; close: string } | null>> | null;
  };
  location: GeoPoint;
  contacts: PublicContacts;
  promotions: ShowcasePromotion[];
  /** От точки запроса, км; без `lat`/`lng` — null */
  distanceKm: number | null;
};
export type PublicNearby = { places: NearbyPlace[]; center: GeoPoint | null };
/** `GET /public/v1/nearby`: без `lat`/`lng` — все точки (сначала с акциями); `radiusKm` — только с центром. */
export const nearbyQuery = z
  .object({
    lat: z.coerce.number().min(-90).max(90).optional(),
    lng: z.coerce.number().min(-180).max(180).optional(),
    radiusKm: z.coerce.number().min(0.1).max(500).optional(),
    deals: z.enum(["1", "0"]).optional(),
    limit: z.coerce.number().int().min(1).max(300).default(200),
  })
  .refine((q) => (q.lat === undefined) === (q.lng === undefined), { error: "Укажите и широту, и долготу", path: ["lat"] });
